<template>
  <div class="w-full">
    <!-- Mismo contenedor y ancho que check-status-procedure -->
    <div class="single-window-container mx-auto px-5 sm:px-6 lg:px-8">
      <!-- Encabezado de la sección -->
      <div class="flex items-center mb-6">
        <img
          :src="trackProcessIcon"
          class="mr-2"
          :style="{ width: '28px', height: '28px' }"
          alt=""
          aria-hidden="true"
        />
        <h2 class="text-blue-dark font-bold text-lg md:text-xl m-0 pl-1">
          Preguntas orientadoras
        </h2>
      </div>

      <!-- Card contenedor: mismo padding que la vista de seguimiento -->
      <div
        class="bg-white rounded-lg shadow-sm border border-gray-200 px-5 py-8 sm:px-8 sm:py-10 md:px-16 md:py-12"
      >
        <!-- Enlace Anterior (alineado a la izquierda) -->
        <div class="text-left">
          <button
            type="button"
            class="inline-flex items-center gap-2 text-base font-semibold text-blue focus:outline-none"
            @click="onBack"
          >
            <img
              :src="returnIcon"
              class="w-5 h-4"
              alt=""
              aria-hidden="true"
            />
            Anterior
          </button>
        </div>

        <!-- Opciones en formato de tarjetas (dos columnas en desktop) -->
        <fieldset class="mt-8 m-0 p-0 border-0">
          <legend class="sr-only">{{ questionLabel }}</legend>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
            <label
              v-for="option in options"
              :key="option.id"
              class="option-card relative flex flex-col gap-3 px-6 py-5 rounded-xl border cursor-pointer transition-colors duration-200"
              :class="
                selected === option.id
                  ? 'bg-white border-blue'
                  : 'bg-white border-blue-light hover:border-blue'
              "
            >
              <input
                type="radio"
                name="sfc-procedure-type"
                class="sr-only"
                :value="option.id"
                :checked="selected === option.id"
                @change="onSelect(option.id)"
              />

              <!-- Título + radio/check en la esquina superior derecha -->
              <div class="flex items-start justify-between gap-3">
                <h3 class="text-blue-dark font-bold text-base md:text-lg leading-snug m-0">
                  {{ option.title }}
                </h3>

                <!-- Indicador de selección -->
                <span
                  class="option-indicator flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full transition-colors duration-200"
                  :class="
                    selected === option.id
                      ? 'bg-blue border-2 border-blue'
                      : 'bg-white border-2 border-gray-300'
                  "
                  aria-hidden="true"
                >
                  <svg
                    v-if="selected === option.id"
                    class="w-3.5 h-3.5 text-white"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 10.5L8.5 14L15 6.5"
                      stroke="currentColor"
                      stroke-width="2.2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              </div>

              <!-- Descripción -->
              <p class="text-left text-sm text-gray-500 leading-relaxed m-0">
                {{ option.description }}
              </p>
            </label>
          </div>
        </fieldset>

        <!-- Banner informativo: solo visible para la opción anónima -->
        <div
          v-if="selected === 'anonymous'"
          class="mt-6 flex items-center gap-3 rounded-xl border border-blue-light bg-blue-light px-5 py-4"
          role="note"
        >
          <svg
            class="flex-shrink-0 text-blue"
            :style="{ width: '30px', height: '30px' }"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
            <path
              d="M12 11v5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
            <circle cx="12" cy="7.75" r="1.1" fill="currentColor" />
          </svg>
          <p class="text-left text-sm text-blue leading-relaxed m-0">
            {{ anonymousNotice }}
          </p>
        </div>

        <!-- Botón Empezar: alineado a la derecha, deshabilitado sin selección -->
        <div class="mt-8 flex justify-end">
          <button
            type="button"
            class="rounded-full px-10 h-10 text-sm font-bold text-white"
            :class="selected ? 'bg-blue hover:bg-blue-dark' : 'bg-gray-400 cursor-not-allowed'"
            :disabled="!selected"
            @click="onStart"
          >
            Empezar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import trackProcessIcon from "@/assets/icons/question-mark.svg";
import returnIcon from "@/assets/icons/return.svg";

export default {
  name: "question_type_fill",
  data() {
    return {
      trackProcessIcon,
      returnIcon,
      questionLabel: "¿Cómo desea radicar su solicitud?",
      anonymousNotice:
        "Cuando se desconozca la información sobre el destinatario, el aviso, con copia íntegra del acto administrativo, se publicará en la página electrónica y en todo caso en un lugar de acceso al público de la respectiva entidad por el término de cinco (5) días, con la advertencia de que la notificación se considerará surtida al finalizar el día siguiente al retiro del aviso.",
      // Opción seleccionada. Vacío = ninguna marcada al cargar.
      selected: "",
      options: [
        {
          id: "with-data",
          title: "Radicar con datos de contacto",
          description:
            "Seleccione esta opción si desea recibir notificaciones automáticas sobre el estado de la solicitud en su correo electrónico.",
        },
        {
          id: "anonymous",
          title: "Radicar de forma anónima",
          description:
            "Seleccione esta opción si prefiere proteger su identidad. No se almacenará ningún dato personal en el sistema.",
        },
      ],
    };
  },
  methods: {
    onSelect(value) {
      this.selected = value;
    },
    onStart() {
      if (!this.selected) return;

      // Cada opción tiene su propio flujo. Hoy ambos redirigen a /login, pero
      // se mantienen separados para poder enviar cada caso a un destino
      // distinto más adelante sin tener que reestructurar el componente.
      if (this.selected === "with-data") {
        this.goToWithData();
      } else if (this.selected === "anonymous") {
        this.goToAnonymous();
      }
    },
    goToWithData() {
      // "Radicar con datos de contacto".
      this.$router.push("/login");
    },
    goToAnonymous() {
      // "Radicar de forma anónima".
      this.$router.push("/login");
    },
    onBack() {
      // Vuelve al paso anterior del flujo.
      this.$router.back();
    },
  },
};
</script>

<style scoped>
/*
 * Mismo ancho máximo del card que en check-status-procedure.
 * No se reutiliza `.container-workspace` porque es `display:flex`.
 */
.single-window-container {
  max-width: 960px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
